import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FilterKeys, GetMiniListQueryFilters, useGetMiniListQuery } from '@schooly/api';
import { SimpleListResult } from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { MINI_LIST } from '@schooly/constants/test-ids';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { Loading, PlusIcon, RollBackIcon } from '@schooly/style';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getSchoolUserId } from '../../../helpers/school';
import { useSchool } from '../../../hooks/useSchool';
import { CREATE_CONDUCT_GROUP_SEARCH_PARAM as CONDUCT_FOR_GROUP_SEARCH_PARAM } from '../../../pages/Conduct/ConductCreateModal/ConductCreateModal';
import { GroupPreviewModalConductSearchInput } from '../../../pages/Groups/GroupPreviewModal/GroupPreviewModalConduct/GroupPreviewModalConductSearchInput';
import { SimpleButton } from '../../uikit/SimpleButton/SimpleButton';
import { ModalContent, ModalPanel } from '../../uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../uikit-components/Modal/ModalHeader';
import { NoSearchResultsFound } from '../NoSearchResultsFound/NoSearchResultsFound';
import PersonCardSelectable from '../PersonCard/PersonCardSelectable';
import { ProfileAccordion } from '../ProfileAccordion/ProfileAccordion';
import {
  AddStudentsFilters,
  AddStudentsFiltersKey,
  AddStudentsFiltersPopup,
  AddStudentsFiltersPopupProps,
} from './AddStudentsFiltersPopup';
import { InlineFiltersTags } from './InlineFiltersTags';

export interface AddStudentsExternalSidebarProps {
  showExcludeGroupsToggle?: boolean;
  headerComponent?: React.ReactNode;
  filtersAvailable?: AddStudentsFiltersKey[];
  deleteHistoryStudents?: SimpleListResult[];
  students: SimpleListResult[];
  onStudentClick: (user?: SimpleListResult | SimpleListResult[]) => void;
  initialFilters?: GetMiniListQueryFilters;
  renderFiltersModal?: (props: AddStudentsFiltersPopupProps) => React.ReactNode;
}

const addStudentsFilters: GetMiniListQueryFilters = {
  [FilterKeys.Status]: [],
  [FilterKeys.AgeGroup]: [],
  [FilterKeys.House]: [],
  [FilterKeys.Group]: [],
  [FilterKeys.Date]: [],
};

const DEFAULT_FILTERS_AVAILABLE: AddStudentsFiltersKey[] = [
  FilterKeys.Status,
  FilterKeys.AgeGroup,
  FilterKeys.House,
  FilterKeys.Group,
];

export const AddStudentsExternalSidebar: React.FC<AddStudentsExternalSidebarProps> = ({
  showExcludeGroupsToggle,
  headerComponent,
  filtersAvailable = DEFAULT_FILTERS_AVAILABLE,
  deleteHistoryStudents,
  students: selectedStudents,
  onStudentClick,
  initialFilters = addStudentsFilters,
  renderFiltersModal = (props) => <AddStudentsFiltersPopup {...props} />,
}) => {
  const { $t } = useIntl();
  const { schoolId } = useSchool();
  const [excludeGroups, setExcludeGroups] = useState(false);
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);
  const [defaultFiltersInitialized, setDefaultFiltersInitialized] = useState(false);
  const { activePropertiesMap, isLoading: isLoadingProperties } = useSchoolProperties(
    {
      schoolId: schoolId || '',
      userType: SchoolUserRole.Student,
      showReEnrollmentProperties: true,
    },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const defaultFilters: AddStudentsFilters | undefined = useMemo(() => {
    if (isLoadingProperties) return;

    const searchParams = new URL(window.location.href).searchParams;
    const groupId = searchParams.get(CONDUCT_FOR_GROUP_SEARCH_PARAM);

    const defaultStatuses =
      activePropertiesMap.status.filter(({ category }) => category?.current) ?? [];
    const defaultStatusesIds = defaultStatuses.map(({ id }) => id);

    return {
      ...initialFilters,
      ...(groupId
        ? {
            [FilterKeys.Group]: [groupId],
          }
        : defaultStatusesIds.length
          ? { [FilterKeys.Status]: defaultStatusesIds }
          : {}),
    };
  }, [isLoadingProperties, activePropertiesMap.status, initialFilters]);

  const { data, params, setParams, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetMiniListQuery(
      {
        schoolId: schoolId || '',
        query: '',
        type: 'student',
        excludeGroups: showExcludeGroupsToggle ? excludeGroups : undefined,
        filters: {}, // Wait for defaultFilters to be initialized
      },
      { enabled: defaultFiltersInitialized, refetchOnMount: 'always' },
    );

  useEffect(() => {
    if (!defaultFilters || !!defaultFiltersInitialized) return;

    setParams((p) => ({ ...p, filters: defaultFilters }));
    setDefaultFiltersInitialized(true);
  }, [defaultFilters, defaultFiltersInitialized, setParams]);

  useEffect(() => {
    setParams((p) => ({ ...p, filters: { ...p.filters, ...initialFilters } }));
  }, [initialFilters, setParams]);

  const filteredList = useMemo(() => {
    const selectedUserIds = selectedStudents.map(getSchoolUserId);
    const entries =
      data?.pages.reduce<SimpleListResult[]>((prev, curr) => {
        prev.push(
          ...curr.results.filter(
            (user) =>
              !selectedUserIds.includes(getSchoolUserId(user)) &&
              !deleteHistoryStudents?.some((u) => u.relation_id === user.relation_id),
          ),
        );
        return prev;
      }, []) ?? [];

    return entries;
  }, [data?.pages, selectedStudents, deleteHistoryStudents]);

  const loaderRef = useInfiniteScroll(isLoading || isFetchingNextPage, fetchNextPage, hasNextPage);

  const handleSetSearchText = useCallback(
    (query: string) => {
      setParams((params) => ({ ...params, query }));
    },
    [setParams],
  );
  const handleSetFilters = useCallback(
    (filters: AddStudentsFilters, newExcludeGroups?: boolean) => {
      if (newExcludeGroups !== undefined) {
        setExcludeGroups(newExcludeGroups);
      }
      setParams((params) => ({
        ...params,
        filters: { ...params.filters, ...filters },
        ...(newExcludeGroups !== undefined ? { excludeGroups: newExcludeGroups } : {}),
      }));
    },
    [setParams],
  );

  const handleResetFilters = useCallback(() => {
    setParams((params) => ({
      ...params,
      filters: initialFilters,
    }));
  }, [initialFilters, setParams]);

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (!filteredList.length && !hasNextPage) {
      return <NoSearchResultsFound />;
    }

    return (
      <>
        {!!deleteHistoryStudents?.length && (
          <Stack pb={2} pt={1}>
            <ProfileAccordion title={$t({ id: 'groups-deleteHistory' })} expandable expandedValue>
              {deleteHistoryStudents?.map((user) => (
                <PersonCardSelectable
                  isUsernameClickable
                  isUsernameLinkView
                  key={user.relation_id}
                  user={user}
                  userType="student"
                  onClick={() => {
                    onStudentClick(user);
                  }}
                  isListItem
                  renderCustomIcon={
                    <IconButton inverse>
                      <RollBackIcon />
                    </IconButton>
                  }
                />
              ))}
            </ProfileAccordion>
          </Stack>
        )}
        {!!filteredList?.length && !!deleteHistoryStudents?.length && (
          <Typography variant="h4">
            <FormattedMessage id="membership-AvailableToAdd" />
          </Typography>
        )}
        {filteredList?.map((user) => (
          <PersonCardSelectable
            isUsernameClickable
            isUsernameLinkView
            key={user.relation_id}
            user={user}
            userType="student"
            onClick={() => onStudentClick(user)}
            isListItem
          ></PersonCardSelectable>
        ))}
        {hasNextPage && (
          <Box py={3}>
            <Loading />
            <div ref={loaderRef} />
          </Box>
        )}
      </>
    );
  };

  const hasFiltersApplied = filtersAvailable.some(
    (key) => !!params.filters[key as keyof typeof initialFilters]?.length,
  );

  const canResetFilters = useMemo(() => {
    return filtersAvailable.some((key) => {
      if (key === FilterKeys.Date) return false;

      const current = params.filters[key] ?? [];
      const target = (initialFilters as AddStudentsFilters)[key] ?? [];

      if (current.length !== target.length) return true;
      return current.some((value) => !target.some((targetValue) => targetValue === value));
    });
  }, [params.filters, filtersAvailable, initialFilters]);

  return (
    <Stack sx={{ height: '100%' }} data-test-id={MINI_LIST.CONTAINER}>
      <ModalHeader title={$t({ id: 'userType-student-plural' })} active />
      <Box px={2.5} pt={2.5}>
        {headerComponent}
        <Stack gap={1}>
          <GroupPreviewModalConductSearchInput
            value={params.query || ''}
            onChangeText={handleSetSearchText}
            hasFilters={hasFiltersApplied}
            onFiltersPress={openFilterModal}
            data-test-id={MINI_LIST.SEARCH_INPUT}
            placeholder={$t(
              { id: 'groups-AvailableUsersSearch' },
              {
                userType: $t({ id: 'userType-student-plural' }).toLowerCase(),
              },
            )}
          />
          <InlineFiltersTags
            filtersAvailable={filtersAvailable}
            onResetFilters={canResetFilters ? handleResetFilters : undefined}
            onOpenFilters={openFilterModal}
            filters={params.filters}
          />
        </Stack>
        {filtersModalVisible &&
          renderFiltersModal({
            excludeGroups: showExcludeGroupsToggle ? excludeGroups : undefined,
            filtersAvailable,
            onClose: closeFiltersModal,
            defaultFilters: initialFilters,
            onSetFilters: handleSetFilters,
            filters: params.filters,
          })}
      </Box>
      <ModalContent active>{renderContent()}</ModalContent>

      {!!filteredList.length && (
        <ModalPanel
          active
          withBorderTop
          sx={{
            px: 2.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 63,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderRadius: 1,
              width: '100%',
            }}
          >
            <SimpleButton
              startIcon={<PlusIcon />}
              onClick={() => onStudentClick(filteredList)}
              data-test-id={MINI_LIST.ADD_ALL_BTN}
            >
              <Typography variant="h3">
                <FormattedMessage
                  id="conduct-AddAllStudents"
                  values={{ studentsCount: filteredList.length }}
                />
              </Typography>
            </SimpleButton>
          </Stack>
        </ModalPanel>
      )}
    </Stack>
  );
};
